import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { store } from 'store/store';
import { CssBaseline, StyledEngineProvider, ThemeProvider } from '@mui/material';
import { orionProviderTheme } from 'aura-material-ui-themes';
import { AppRouter } from 'routes/AppRouter';
import { ToastProvider } from 'components/toasts/ToastProvider';
import { AuthProvider } from '@resmed/identity-spa-auth-react';
import { environmentConfig } from 'environmentConfig';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { routes } from 'routes/routes';
import { createTheme } from '@mui/material/styles';
import { logger } from 'utils/logger';
import './index.css';
const theme = createTheme(orionProviderTheme);
const RETURN_TO_KEY = 'identity-my-account:returnTo';
const saveReturnToPath = () => {
    var _a, _b, _c, _d, _e;
    try {
        const returnTo = (_c = (_b = (_a = globalThis.window) === null || _a === void 0 ? void 0 : _a.location) === null || _b === void 0 ? void 0 : _b.hash) !== null && _c !== void 0 ? _c : '';
        const path = returnTo.startsWith('#') ? returnTo.slice(1) : returnTo;
        if (!path.startsWith('/') || path.startsWith('//') || path.includes('://'))
            return;
        (_e = (_d = globalThis.sessionStorage) === null || _d === void 0 ? void 0 : _d.setItem) === null || _e === void 0 ? void 0 : _e.call(_d, RETURN_TO_KEY, returnTo);
    }
    catch (_f) {
        // Ignore storage errors (e.g. disabled or unavailable).
    }
};
const consumeReturnToPath = () => {
    var _a, _b, _c, _d;
    try {
        const returnTo = (_b = (_a = globalThis.sessionStorage) === null || _a === void 0 ? void 0 : _a.getItem) === null || _b === void 0 ? void 0 : _b.call(_a, RETURN_TO_KEY);
        if (!returnTo)
            return null;
        (_d = (_c = globalThis.sessionStorage) === null || _c === void 0 ? void 0 : _c.removeItem) === null || _d === void 0 ? void 0 : _d.call(_c, RETURN_TO_KEY);
        const path = returnTo.startsWith('#') ? returnTo.slice(1) : returnTo;
        if (!path.startsWith('/') || path.startsWith('//') || path.includes('://'))
            return null;
        return returnTo;
    }
    catch (_e) {
        return null;
    }
};
// Map app environment names to SDK environment names
const mapEnvironment = (env) => {
    const envMap = {
        local: 'local',
        'dev-dit-amr': 'dev-dit-amr',
        'dev-fit-amr': 'dev-fit-amr',
        'e2e-perf-amr': 'e2e-perf-amr',
        'e2e-stg-amr': 'stg-amr',
        'prod-amr': 'prod-amr'
    };
    if (Object.prototype.hasOwnProperty.call(envMap, env)) {
        return envMap[env];
    }
    // Fail fast on unsupported environment to prevent misconfiguration
    const errorMsg = `Unsupported environment value '${env}' in environmentConfig.env`;
    logger.error(errorMsg);
    throw new Error(errorMsg);
};
const Entry = () => {
    const [isCreateAccountRoute, setIsCreateAccountRoute] = useState(() => {
        var _a, _b, _c;
        const hash = (_b = (_a = globalThis.window) === null || _a === void 0 ? void 0 : _a.location) === null || _b === void 0 ? void 0 : _b.hash;
        return (_c = hash === null || hash === void 0 ? void 0 : hash.startsWith(`#${routes.CREATE_ACCOUNT}`)) !== null && _c !== void 0 ? _c : false;
    });
    useEffect(() => {
        var _a, _b;
        const handleHashChange = () => {
            var _a, _b, _c;
            const hash = (_b = (_a = globalThis.window) === null || _a === void 0 ? void 0 : _a.location) === null || _b === void 0 ? void 0 : _b.hash;
            setIsCreateAccountRoute((_c = hash === null || hash === void 0 ? void 0 : hash.startsWith(`#${routes.CREATE_ACCOUNT}`)) !== null && _c !== void 0 ? _c : false);
        };
        (_b = (_a = globalThis.window) === null || _a === void 0 ? void 0 : _a.addEventListener) === null || _b === void 0 ? void 0 : _b.call(_a, 'hashchange', handleHashChange);
        return () => {
            var _a, _b;
            (_b = (_a = globalThis.window) === null || _a === void 0 ? void 0 : _a.removeEventListener) === null || _b === void 0 ? void 0 : _b.call(_a, 'hashchange', handleHashChange);
        };
    }, []);
    const appContent = _jsx(AppRouter, {});
    return (_jsx(React.StrictMode, { children: _jsx(StyledEngineProvider, { injectFirst: true, children: _jsx(ToastProvider, { children: _jsxs(ThemeProvider, { theme: theme, children: [_jsx(CssBaseline, {}), _jsx(Provider, { store: store, children: _jsx(AuthProvider, { clientId: environmentConfig.oktaClientId, environment: mapEnvironment(environmentConfig.env), onPageLoadOptions: {
                                    // Do not trigger automatic login/redirect for the create-account route,
                                    // so unauthenticated users can access it directly.
                                    triggerLoginFlowOnUnauthenticated: !isCreateAccountRoute,
                                    redirectToLogin: !isCreateAccountRoute,
                                    preLoginHook: saveReturnToPath
                                }, onAuthenticated: (claims) => {
                                    var _a;
                                    try {
                                        const returnTo = consumeReturnToPath();
                                        if (returnTo && ((_a = globalThis.window) === null || _a === void 0 ? void 0 : _a.location)) {
                                            if (globalThis.window.location.hash !== returnTo) {
                                                globalThis.window.location.hash = returnTo;
                                            }
                                        }
                                    }
                                    catch (_b) {
                                        // Ignore navigation errors and continue with auth handling.
                                    }
                                    if (claims === null || claims === void 0 ? void 0 : claims.subject) {
                                        datadogRum.setUser({ id: claims.subject });
                                        datadogLogs.setUser({ id: claims.subject });
                                        logger.info('AuthProvider authenticated user', { hasSubject: true });
                                    }
                                    else {
                                        logger.warn('AuthProvider authenticated without subject claim');
                                    }
                                }, onError: (error) => {
                                    logger.error('Auth error', { error });
                                }, children: appContent }) })] }) }) }) }));
};
export default Entry;
